
import type { PropType } from 'vue'
import { defineComponent, ref, computed } from 'vue'

// > composable <
import useTooltip from '@/modern/composable/useTooltip'
import useClickOutside from '@/modern/composable/useClickOutside'

// > legacy/validation <
import useValidationMessages from '@/common/composable/useValidationMessages'
import type { ErrorObject } from '@vuelidate/core'

export default defineComponent({
    props: {
        size: {
            type: String as PropType<'medium' | 'big'>,
            default: 'medium'
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: String as PropType<string | null>,
            default: null
        },
        placeholder: {
            type: String,
            default: null
        },
        displayFormat: {
            type: String as PropType<'YYYY-MM-DD' | 'DD.MM.YYYY'>,
            default: 'DD.MM.YYYY'
        },
        outputFormat: {
            type: String as PropType<'YYYY-MM-DD' | 'DD.MM.YYYY'>,
            default: 'YYYY-MM-DD'
        },
        closeAfterSelect: {
            type: Boolean,
            default: true
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        validationErrors: {
            type: Array as PropType<ErrorObject[]>,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
        const { getValidationMessage } = useValidationMessages()

        const containerReference = ref<HTMLElement | null>(null)

        const { reference, tooltip, isVisible, x, y, strategy, open, close } = useTooltip({
            placement: 'bottom-start',
            strategy: 'absolute',
            offset: 4,
            autoPlacement: {
                allowedPlacements: ['bottom-start', 'top-start'],
            },
        })

        useClickOutside(containerReference, close)

        const toggleCalendar = () => {
            if (props.disabled) return

            if (isVisible.value) {
                close()
            } else {
                open()
            }
        }

        const onUpdate = (date: string) => {
            if (props.disabled) return

            emit('update:modelValue', date)
            emit('change', date)

            if (props.closeAfterSelect) {
                close()
            }
        }

        const parseDate = (value: string) => {
            if (props.outputFormat === 'DD.MM.YYYY') {
                const [dd, mm, yyyy] = value.split('.').map(Number)
                return new Date(yyyy, mm - 1, dd, 0, 0, 0, 0)
            }

            const [yyyy, mm, dd] = value.split('-').map(Number)
            return new Date(yyyy, mm - 1, dd, 0, 0, 0, 0)
        }

        const formatDate = (date: Date) => {
            const yyyy = date.getFullYear()
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            const dd = String(date.getDate()).padStart(2, '0')

            if (props.displayFormat === 'DD.MM.YYYY') {
                return `${dd}.${mm}.${yyyy}`
            }

            return `${yyyy}-${mm}-${dd}`
        }

        const displayValue = computed(() => {
            if (!props.modelValue) return null

            const date = parseDate(props.modelValue)
            return formatDate(date)
        })

        return {
            containerReference,
            reference,
            tooltip,
            isVisible,
            x,
            y,
            strategy,
            toggleCalendar,
            onUpdate,
            displayValue,
            getValidationMessage,
        }
    }
})
